import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Orders from './pages/Orders';
import Products from './pages/Products';
import Settings from './pages/Settings';
import Automations from './pages/Automations';
import Trends from './pages/Trends';
import CompetitorScan from './pages/CompetitorScan';
import Legal from './pages/Legal';
import Billing from './pages/Billing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';
import ConfirmEmail from './pages/ConfirmEmail';
import ApiDocs from './pages/ApiDocs';
import UpdateBanner from './components/UpdateBanner';
import InstallButton from './components/InstallButton';
import Landing from './pages/Landing';
import ProfitSummary from './pages/ProfitSummary';
import DashboardStats from './components/DashboardStats';
import { API_URL } from './config';
import { formatMoney } from './utils/money';

const TOKEN_KEY = 'omniflow_token';
function Dashboard({ token, onAuthError }) {
  const { t, i18n } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(() => fetchOrders({ silent: true }), 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async ({ silent = false } = {}) => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/orders/recent?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data.data || [];
      setOrders(data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        onAuthError();
      } else if (!silent) {
        console.error('Error:', error);
      }
    } finally {
      setLoading(false);
    }
  };


  const columns = [
    t('dashboard.colPlatform'), t('dashboard.colOrderId'), t('dashboard.colGross'),
    t('dashboard.colNet'), t('dashboard.colMargin'), t('dashboard.colStatus'),
  ];

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h2 className="text-white text-2xl font-bold">{t('dashboard.title')}</h2>
          <p className="text-muted text-sm mt-1">{t('dashboard.subtitle')}</p>
        </div>
        <div className="self-start sm:self-auto bg-surfaceAlt border border-edge rounded-lg px-4 py-2 text-muted text-[13px]">
          {new Date().toLocaleDateString(i18n.language)}
        </div>
      </div>

      <DashboardStats token={token} onAuthError={onAuthError} />

      <ProfitSummary token={token} onAuthError={onAuthError} />

      <div className="bg-surface border border-edge rounded-2xl p-4 sm:p-6">
        <h3 className="text-white text-base font-semibold mb-6">{t('dashboard.recentOrders')}</h3>
        {loading ? (
          <p className="text-muted text-center py-10">{t('dashboard.loading')}</p>
        ) : orders.length === 0 ? (
          <p className="text-muted text-center py-10">{t('dashboard.noOrdersYet')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] border-collapse">
              <thead>
                <tr className="border-b border-edge">
                  {columns.map(h => (
                    <th key={h} className="text-muted text-xs text-left pb-3 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const lang = i18n.language;
                  const showProfit = order.counted && !order.cost_missing;
                  const hint = !order.counted ? t('dashboard.notCounted') : order.cost_missing ? t('dashboard.costMissing') : undefined;
                  const statusKey = { paid: 'statusPaid', pending: 'statusPending', shipped: 'statusShipped', cancelled: 'statusCancelled' }[order.status];
                  const margin = order.real_margin;
                  return (
                    <tr key={order.id} className="border-b border-surfaceAlt">
                      <td className="py-4 text-white text-sm capitalize">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-accent"></div>
                          {order.source_platform}
                        </div>
                      </td>
                      <td className="py-4 text-[#888] text-[13px] font-mono">{order.platform_order_id}</td>
                      <td className="py-4 text-white text-sm font-semibold">{formatMoney(order.total_gross, lang, order.currency)}</td>
                      <td className={`py-4 text-sm font-semibold ${showProfit ? (order.real_profit >= 0 ? 'text-accent' : 'text-red-400') : 'text-muted'}`} title={hint}>
                        {showProfit ? formatMoney(order.real_profit, lang, order.currency) : '\u2014'}
                      </td>
                      <td className={`py-4 text-sm font-semibold ${!showProfit || margin === null ? 'text-muted' : margin >= 20 ? 'text-accent' : 'text-amber-500'}`} title={hint}>
                        {showProfit && margin !== null ? `${margin.toLocaleString(lang)} %` : '\u2014'}
                      </td>
                      <td className="py-4">
                        <span className={`px-3 py-1 rounded-full text-xs border ${order.status === 'cancelled' ? 'bg-[#2a0f0f] text-red-400 border-[#7f1d1d]' : order.status === 'pending' ? 'bg-[#2a1f0f] text-amber-400 border-[#7f5f1d]' : 'bg-[#0f2a0f] text-[#4ade80] border-[#166534]'}`}>
                          {statusKey ? t(`orders.${statusKey}`) : order.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MenuIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

function Layout({ token, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/v1/tenant`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTenant(response.data.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          onLogout();
        }
      }
    };
    fetchTenant();
  }, [token, onLogout]);

  const PLAN_LABEL_KEYS = {
    none: 'sidebar.noPlan',
    starter: 'sidebar.planStarter',
    pro: 'sidebar.planPro',
    enterprise: 'sidebar.planEnterprise',
  };

  const menuItems = [
    { icon: '\u25A6', label: t('nav.dashboard'), path: '/dashboard' },
    { icon: '\u25C8', label: t('nav.orders'), path: '/orders' },
    { icon: '\u25C9', label: t('nav.products'), path: '/products' },
    { icon: '\uD83D\uDCC8', label: t('nav.trends'), path: '/trends' },
    { icon: '\uD83D\uDD0D', label: t('nav.competitor'), path: '/competitor' },
    { icon: '\u26A1', label: t('nav.automations'), path: '/automations' },
    { icon: '$', label: t('nav.billing'), path: '/billing', adminOnly: true },
    { icon: '\u2699', label: t('nav.settings'), path: '/settings' },
  ].filter((item) => !item.adminOnly || (tenant && tenant.role === 'admin'));

  const goTo = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const hasNoPlan = tenant && tenant.plan_type === 'none';
  const planLabel = tenant ? t(PLAN_LABEL_KEYS[tenant.plan_type] || tenant.plan_type) : t('dashboard.loading');

  return (
    <div className="bg-panel min-h-screen font-sans">
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-surface border-b border-edge flex items-center justify-between px-4 z-30">
        <span className="text-accent text-lg font-extrabold">{t('common.appName')}</span>
        <button
          onClick={() => setSidebarOpen(true)}
          className="text-white p-1"
          aria-label="Abrir menu"
        >
          <MenuIcon />
        </button>
      </div>

      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div
        className={`fixed left-0 top-0 bottom-0 w-60 bg-surface border-r border-edge py-6 z-50 flex flex-col overflow-y-auto
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
      >
        <div className="px-6 pb-8 flex items-center justify-between">
          <div>
            <h1 className="text-accent text-lg font-extrabold leading-tight">{t('common.appName')}</h1>
            <p className="text-muted text-[11px] mt-1">{t('common.tagline')}</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden text-muted"
            aria-label="Cerrar menu"
          >
            <CloseIcon />
          </button>
        </div>
        {menuItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <div
              key={item.path}
              onClick={() => goTo(item.path)}
              className={`px-6 py-3 flex items-center gap-3 cursor-pointer text-sm border-l-[3px]
                ${active ? 'bg-surfaceAlt border-accent text-accent' : 'border-transparent text-muted'}`}
            >
              <span className="text-base">{item.icon}</span>
              {item.label}
            </div>
          );
        })}
        <div className="mt-auto px-6 pt-4">
          <InstallButton />
          <div
            onClick={() => goTo('/billing')}
            className={`bg-surfaceAlt rounded-xl p-4 border mb-2.5 cursor-pointer ${hasNoPlan ? 'border-amber-500' : 'border-edge'}`}
          >
            <p className={`text-xs font-semibold ${hasNoPlan ? 'text-amber-500' : 'text-accent'}`}>
              {planLabel}
            </p>
            <p className="text-muted text-[11px] mt-1">{tenant ? tenant.name : ''}</p>
          </div>
          <button
            onClick={onLogout}
            className="w-full bg-transparent border border-edge rounded-lg py-2.5 text-muted text-[13px] cursor-pointer"
          >
            {t('nav.logout')}
          </button>
        </div>
      </div>

      <div className="md:ml-60 pt-20 md:pt-8 px-4 sm:px-6 md:px-8 pb-8">
        {tenant && tenant.is_owner && tenant.email_verified === false && location.pathname !== '/settings' && (
          <div className="bg-[#2a1f0f] border border-[#7f5f1d] rounded-lg p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-amber-400 text-sm">{t('settings.emailNotVerified')}</p>
            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="bg-transparent border border-amber-400 text-amber-400 text-xs px-3 py-1.5 rounded cursor-pointer shrink-0"
            >
              {t('nav.verifyEmailNow')}
            </button>
          </div>
        )}
        <Routes>
          <Route path="/dashboard" element={<Dashboard token={token} onAuthError={onLogout} />} />
          <Route path="/orders" element={<Orders token={token} onAuthError={onLogout} tenant={tenant} />} />
          <Route path="/products" element={<Products token={token} onAuthError={onLogout} tenant={tenant} />} />
          <Route path="/trends" element={<Trends token={token} onAuthError={onLogout} />} />
          <Route path="/competitor" element={<CompetitorScan token={token} onAuthError={onLogout} />} />
          <Route path="/automations" element={<Automations token={token} onAuthError={onLogout} tenant={tenant} />} />
          <Route path="/billing" element={<Billing token={token} onAuthError={onLogout} />} />
          <Route path="/settings" element={<Settings token={token} onAuthError={onLogout} onTenantChange={(d) => setTenant((prev) => ({ ...prev, ...d }))} />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));

  const handleLoginSuccess = (newToken) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <Router>
      <UpdateBanner />
      <Routes>
        <Route
          path="/"
          element={token ? <Navigate to="/dashboard" replace /> : <Landing />}
        />
        <Route
          path="/login"
          element={token ? <Navigate to="/dashboard" replace /> : <Login onLoginSuccess={handleLoginSuccess} />}
        />
        <Route
          path="/register"
          element={token ? <Navigate to="/dashboard" replace /> : <Register onLoginSuccess={handleLoginSuccess} />}
        />
        <Route
          path="/forgot-password"
          element={token ? <Navigate to="/dashboard" replace /> : <ForgotPassword />}
        />
        <Route
          path="/reset-password"
          element={token ? <Navigate to="/dashboard" replace /> : <ResetPassword />}
        />
        <Route
          path="/accept-invite"
          element={token ? <Navigate to="/dashboard" replace /> : <AcceptInvite onLoginSuccess={handleLoginSuccess} />}
        />
        <Route path="/confirm-email" element={<ConfirmEmail />} />
        <Route path="/developers" element={<ApiDocs />} />
        <Route path="/aviso-legal" element={<Legal />} />
        <Route path="/privacidad" element={<Legal />} />
        <Route path="/cookies" element={<Legal />} />
        <Route
          path="/*"
          element={token ? <Layout token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />}
        />
      </Routes>
    </Router>
  );
}

export default App;